import React, { useState, useEffect } from 'react'
import debounce from 'lodash/debounce'
import { Icon } from '@scentregroup/ui'
import style from './index.module.scss'

const DEBOUNCED_WAIT = 400

type Props = {
  onQueryKeyDown: React.KeyboardEventHandler<unknown>
  onSubmit: (event: React.FormEvent<unknown>, query: string) => void
  onClickClear: () => void
  onClickClose: () => void
  queryText?: string
  onQueryChange: (newQuery: string) => void
}

function SearchModalControls(props: Props): JSX.Element {
  const {
    onQueryKeyDown,
    onSubmit,
    onClickClear,
    onClickClose,
    queryText,
  } = props

  const [query, setQuery] = useState(queryText || '')

  const onQueryChangeDebounced = debounce(query => {
    props.onQueryChange(query)
  }, DEBOUNCED_WAIT)

  const handleKeyPress: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      // Trigger blur event to dismiss the Keyboard when user clicks Search in iOS.
      if (!window.document.activeElement) {
        return
      }

      ;(window.document.activeElement as HTMLInputElement).blur()
    }
  }

  useEffect(() => {
    onQueryChangeDebounced(query)
  }, [query])

  function onSubmitWrapper(e: React.FormEvent<unknown>): void {
    onSubmit(e, query)
  }

  return (
    <>
      <button
        aria-label="Close search"
        className={style.closeButton}
        onClick={onClickClose}
      >
        <Icon type="cross" />
      </button>
      <div className={style.inputbar} onSubmit={onSubmitWrapper}>
        <form action="#" onSubmit={e => e.preventDefault()}>
          <input
            /* Disabling no-autofocus to convert legacy JS file to TypeScript */
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            id="search-textfield"
            type="search"
            className={style.textfield}
            autoCapitalize="off"
            spellCheck="false"
            autoCorrect="off"
            autoComplete="off"
            placeholder="Enter search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={onQueryKeyDown}
            onKeyPress={handleKeyPress}
          />
          <label htmlFor="search-textfield">
            <Icon type="search" className={style.searchIcon} />
          </label>
          {queryText ? (
            <button
              aria-label="Clear search text"
              className={style.clearButton}
              type="button"
              onClick={onClickClear}
            >
              <Icon type="clear" size="small" className={style.clearIcon} />
            </button>
          ) : null}
        </form>
      </div>
    </>
  )
}

SearchModalControls.defaultProps = {
  queryText: '',
}

export default SearchModalControls
